import { useEffect, useSyncExternalStore } from 'react';

import type { LocalStorageEntries } from 'lib/storage/localStorage';
import { localStorage } from 'lib/storage/localStorage';

let callbacks: (() => void)[] = [];
const emitChange = () => {
  for (const callback of callbacks) {
    callback();
  }
};
const subscribe = (callback: () => void) => {
  callbacks = [...callbacks, callback];
  window.addEventListener('storage', callback);
  return () => {
    callbacks = callbacks.filter(l => l !== callback);
    window.removeEventListener('storage', callback);
  };
};

const snapshot: Record<string, unknown> = {};

export const useLocalStorage = <K extends keyof LocalStorageEntries>(
  key: K,
  initialValue: LocalStorageEntries[K]
) => {
  const store = useSyncExternalStore(subscribe, () => {
    if (!(key in snapshot)) {
      snapshot[key] = localStorage.getItem(key);
    }
    return snapshot[key] as LocalStorageEntries[K];
  });
  const setStore = (
    value:
      | (LocalStorageEntries[K] | null)
      | ((previousValue: LocalStorageEntries[K]) => LocalStorageEntries[K])
  ) => {
    localStorage.setItem(
      key,
      value instanceof Function ? value(store || initialValue) : value
    );
    snapshot[key] = localStorage.getItem(key);
    emitChange();
  };

  useEffect(() => {
    if (store === null && initialValue) {
      localStorage.setItem(key, initialValue);
    }
  }, [store, initialValue, key]);

  return [store ?? initialValue, setStore] as const;
};

// For testing purposes: reset module-level state
useLocalStorage.clear = () => {
  callbacks = [];
  Object.keys(snapshot).forEach(key => {
    delete snapshot[key];
  });
};
